import React, {useContext, useEffect, useState} from "react";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {toast} from "react-toastify";
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box, Button, Card, CardActions, CardContent, Chip, Dialog, DialogActions,
  DialogContent,
  DialogTitle, Grid,
  IconButton,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Delete02Icon, GridViewIcon, ListViewIcon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import SearchInput from "../../components/inputs/SearchInput";
import axios from "../../config/axios";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import moment from "moment";

const VIEW_TABLE = "table";
const VIEW_CARDS = "cards";
const VIEW_MODE_KEY = "organizations-view-mode";

const getStoredViewMode = () => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === VIEW_CARDS ? VIEW_CARDS : VIEW_TABLE;
  } catch (e) {
    return VIEW_TABLE;
  }
}

export const Organizations = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [viewMode, setViewMode] = useState(getStoredViewMode);
  const [query, setQuery] = useState("");
  const {currentUser, organizations, updateOrganizations} = useContext(SignInContext);

  const search = query.trim().toLowerCase();
  const filteredOrganizations = (organizations || []).filter(organization => !search
    || organization?.name?.toLowerCase()?.includes(search)
    || organization?.type?.toLowerCase()?.includes(search)
    || organization?.members?.some(m => m?.member?.toLowerCase()?.includes(search)));

  const confirmDelete = useConfirm(
    'Are you sure you want to delete this organization?',
  );

  useEffect(() => {
    if (!organizations?.length) {
      updateOrganizations();
    }
  }, [])


  useEffect(() => {
    console.log(organizations)
  }, [organizations])
  const onToggleActive = (organization) => {
    OrganizationRepository.edit(organization?.id, {
      name: organization?.name,
      type: organization?.type,
      active: !organization?.active
    })
      .then(() => {
        updateOrganizations();
      })
      .catch(error => {
        toast.error("Failed to update organization status");
      })
  }

  const onEdit = (organization) => {
    setSelectedOrganization(organization);
    setOpenModal(true);
  }

  const onChangeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch (e) {
      // view preference is optional
    }
  }

  const handleDelete = async (id) => {
    const response = await confirmDelete({title: "Are you sure you want to delete this organization?"});
    if (response) {
      OrganizationRepository.delete(id)
        .then(() => {
          toast.success("Successfully deleted organization");
          updateOrganizations();
        })
        .catch(error => {
          toast.error("Failed to delete organization");
        })
    }
  }

  return (
    <>
      <CreateOrganization
        handleClose={() => {
          setOpenModal(false);
          setSelectedOrganization(null);
        }}
        open={openModal}
        selectedOrganization={selectedOrganization}
        onSuccess={updateOrganizations}
      />
      <Grid maxWidth={"lg"} sx={{mx: "auto", p: 3, my: 5, borderRadius: 4, bgcolor: "#fefefeee"}} container className={"border-shadow"}>
        <Grid item xs={12}>
          <Stack direction={{xs: "column", md: "row"}} gap={2} alignItems={{xs: "stretch", md: "center"}}>
            <Typography variant={"h4"} fontWeight={600} sx={{flexShrink: 0}}>Organizations</Typography>
            <SearchInput value={query} onChange={setQuery} placeholder={"Search organizations..."}/>
            <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"} sx={{flexShrink: 0}}>
              <ToggleButtonGroup
                value={viewMode} exclusive
                onChange={(e, val) => val && onChangeViewMode(val)}
                size="small"
                sx={{
                  gap: 0.5,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 2,
                    px: 1.5, py: 0.75,
                    color: "text.secondary",
                    "&:hover": {bgcolor: "grey.300"},
                    "&.Mui-selected": {bgcolor: "grey.200", color: "grey.700"},
                  },
                }}
              >
                <ToggleButton value={VIEW_TABLE} aria-label={"Table view"}>
                  <Tooltip title={"Table view"}>
                    <HugeiconsIcon icon={ListViewIcon} size={18}/>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={VIEW_CARDS} aria-label={"Card view"}>
                  <Tooltip title={"Card view"}>
                    <HugeiconsIcon icon={GridViewIcon} size={18}/>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                sx={{textWrap: "nowrap", flexShrink: 0}} variant={"primary"}
                startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}                onClick={() => {
                  setOpenModal(true);
                  setSelectedOrganization(null);
                }}
              >
                Create Organization
              </Button>
            </Stack>
          </Stack>
        </Grid>
        {viewMode === VIEW_CARDS ? (
          <Grid item xs={12}>
            <Grid container spacing={2} sx={{mt: 1}}>
              {filteredOrganizations.map(organization => (
                <Grid item xs={12} sm={6} md={4} key={organization?.id}>
                  <OrganizationCard
                    organization={organization}
                    onToggleActive={() => onToggleActive(organization)}
                    onEdit={() => onEdit(organization)}
                    onDelete={() => handleDelete(organization?.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 700}}>Name</TableCell>
                <TableCell sx={{fontWeight: 700}}>Type</TableCell>
                <TableCell sx={{fontWeight: 700}}>Members</TableCell>
                <TableCell sx={{fontWeight: 700}}>Date Created</TableCell>
                <TableCell sx={{fontWeight: 700}}>Active</TableCell>
                <TableCell sx={{fontWeight: 700}}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrganizations.map(organization => {
                return (
                  <TableRow key={organization?.id}>

                    <TableCell>
                      <Typography>{organization?.name}</Typography>
                      <Typography variant={"body2"} fontSize={11} noWrap>{organization?.description}</Typography>
                    </TableCell>
                    <TableCell>{organization?.type}</TableCell>
                    <TableCell>
                      <MemberAvatars members={organization?.members?.map(i => i?.member) || []}/>
                    </TableCell>
                    <TableCell>
                      {moment(organization?.dateCreated).format("DD MMM YYYY HH:mm")}
                    </TableCell>
                    <TableCell padding={"checkbox"}>
                      <Switch
                        checked={!!organization?.active}
                        onChange={() => onToggleActive(organization)}
                      />
                    </TableCell>
                    <TableCell padding={"checkbox"}>
                      <Stack direction={"row"} gap={1} justifyContent={"space-between"} sx={{pb: 0.5}}
                             alignItems={"flex-start"}>
                        <IconButton
                          variant={"contained"} sx={{borderRadius: "50% !important"}}
                          onClick={() => onEdit(organization)}>
                          <HugeiconsIcon icon={PencilEdit01Icon} size={18} />
                        </IconButton>
                        <IconButton
                          variant={"contained"} color={"error"}
                          sx={{borderRadius: "50% !important"}}
                          onClick={() => handleDelete(organization?.id)}>
                          <HugeiconsIcon icon={Delete02Icon} size={18} />
                        </IconButton>
                      </Stack>

                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Grid>
        )}
        {query && filteredOrganizations.length === 0 && (
          <Grid item xs={12}>
            <Typography color={"text.secondary"} textAlign={"center"} sx={{py: 4}}>
              No organizations match "{query}"
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  )
}

const MAX_AVATARS = 4;

const MemberAvatars = ({members}) => {
  if (!members?.length) {
    return <Typography variant={"body2"} color={"text.secondary"}>-</Typography>;
  }

  return (
    <AvatarGroup
      max={MAX_AVATARS}
      sx={{justifyContent: "flex-end", "& .MuiAvatar-root": {width: 32, height: 32, fontSize: 14}}}
      renderSurplus={(surplus) => (
        <Tooltip title={members.slice(MAX_AVATARS - 1).join(", ")}>
          <span>+{surplus}</span>
        </Tooltip>
      )}
    >
      {members.map(member => (
        <Tooltip title={member} key={member}>
          <Avatar alt={member} sx={{bgcolor: grey[200], color: "secondary.main"}}>
            {member?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Tooltip>
      ))}
    </AvatarGroup>
  )
}

const OrganizationCard = ({organization, onToggleActive, onEdit, onDelete}) => {
  return (
    <Card variant={"outlined"} sx={{borderRadius: 3, height: "100%", display: "flex", flexDirection: "column"}}>
      <CardContent sx={{flex: 1, display: "flex", flexDirection: "column", gap: 1.5}}>
        <Stack direction={"row"} gap={1} justifyContent={"space-between"} alignItems={"flex-start"}>
          <Box sx={{minWidth: 0}}>
            <Typography fontWeight={600} noWrap>{organization?.name}</Typography>
            {organization?.type && (
              <Chip label={organization?.type} size={"small"} sx={{mt: 0.5, bgcolor: "grey.100"}}/>
            )}
          </Box>
          <Tooltip title={organization?.active ? "Active" : "Inactive"}>
            <Switch size={"small"} checked={!!organization?.active} onChange={onToggleActive}/>
          </Tooltip>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{mt: "auto"}}>
          <MemberAvatars members={organization?.members?.map(i => i?.member) || []}/>
          <Typography variant={"caption"} color={"text.secondary"}>
            {moment(organization?.dateCreated).format("DD MMM YYYY")}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions sx={{justifyContent: "flex-end", borderTop: 1, borderColor: "divider", px: 2}}>
        <Button size={"small"} sx={{boxShadow: 0}} onClick={onEdit}
                startIcon={<HugeiconsIcon icon={PencilEdit01Icon} size={16}/>}>
          Edit
        </Button>
        <Button size={"small"} color={"error"} sx={{boxShadow: 0}} onClick={onDelete}
                startIcon={<HugeiconsIcon icon={Delete02Icon} size={16}/>}>
          Delete
        </Button>
      </CardActions>
    </Card>
  )
}

export const CreateOrganization =({open, handleClose, selectedOrganization, onSuccess}) => {
  const [organization, setOrganization] = useState({name: "", type: "", members: [], active: true});
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get('/api/user/all');
        const users = response.data?.filter(u => u?.enabled);
        console.log(users);
        setUsers([...(new Set(users?.map(u => u.username)) || [])]);
      } catch (err) {
        toast.error("Failed to get users")
      }
    }
    fetchUsers();
  }, [])


  useEffect(() => {
    setOrganization({
      name: selectedOrganization?.name || "",
      type: selectedOrganization?.type || "",
      active: selectedOrganization?.active ?? true,
      members: selectedOrganization?.members?.map(item => item?.member) || []
    });
  }, [selectedOrganization])

  const onChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    onUpdate(key, value);
  }

  const onUpdate = (key, value) => {
    setOrganization(prev => ({...prev, [key]: value}))
  }

  const updateMembers = async (id) => {
    const currentMembers = selectedOrganization?.members || [];
    const added = organization.members.filter(username => !currentMembers.some(m => m?.member === username));
    const removed = currentMembers.filter(m => !organization.members.includes(m?.member));

    await Promise.all([
      ...added.map(username => OrganizationRepository.addMember(id, username)),
      ...removed.map(m => OrganizationRepository.removeMember(id, m?.id))
    ]);
  }

  const handleAddMember = (newUser) => {
    if (newUser && !organization?.members?.includes(newUser)) {
      setOrganization(prev => ({
        ...prev,
        members: [...(prev.members || []), newUser]
      }));
      setSelectedUser(null);
    }
  }

  const handleRemoveMember = (memberToRemove) => {
    setOrganization(prev => ({
      ...prev,
      members: prev.members.filter(member => member !== memberToRemove)
    }));
  }

  const availableUsers = users.filter(user => !organization?.members?.includes(user));
  console.log(users, availableUsers, organization.members)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        sx: {minWidth: "320px", maxWidth: "sm", borderRadius: 4, p: 2, width: "100%", maxHeight: "65dvh", overflow: "hidden"},
        onSubmit: async (event) => {
          event.preventDefault();
          if (!organization?.name) {
            toast.error("Organization name is required!")
            return;
          }
          if (selectedOrganization?.id) {
            const {members, ...details} = organization;
            OrganizationRepository.edit(selectedOrganization?.id, details)
              .then(() => updateMembers(selectedOrganization?.id))
              .then(response => {
                onSuccess();
                toast.success("Successfully updated organization");
                handleClose();
              })
              .catch(error => {
                toast.error(error?.message || "Failed to update organization");
              })
          } else {
            OrganizationRepository.create(organization)
              .then(response => {
                onSuccess();
                toast.success("Successfully created organization");
                handleClose();
              })
              .catch(error => {
                toast.error(error?.message || "Failed to create organization");
              })
          }
        },
      }}
    >
      <DialogTitle>{selectedOrganization?.id ? "Update" : "Create"} Organization</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, py: "16px !important"}}>
          <TextField
            label={"Name"}
            required
            fullWidth
            name={"name"}
            value={organization?.name}
            onChange={onChange}
          />
          <TextField
            label={"Type"}
            required
            fullWidth
            name={"type"}
            value={organization?.type}
            onChange={onChange}
          />

          <Autocomplete
            renderInput={(props) => <TextField {...props} label={`Add Member`} placeholder="Search users..."/>}
            options={availableUsers}
            getOptionLabel={option => option}
            value={selectedUser}
            clearOnBlur
            blurOnSelect
            onChange={(e, newValue) => {
              handleAddMember(newValue);
            }}
            onBlur={() => setSelectedUser(null)}
          />
          <Box sx={{flex: 1, overflowY: "auto"}}>
            {/*<Typography variant={"body2"} sx={{ mb: 1, fontWeight: 600 }}>Members</Typography>*/}


            {organization?.members?.length > 0 && (
              <Table size="small" sx={{ mt: 2, border: 0, borderColor: "divider", borderRadius: "8px !important" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organization?.members?.map((member, index) => (
                    <TableRow key={index}>
                      <TableCell>{member}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(member)}
                        >
                          <HugeiconsIcon icon={Delete02Icon} size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
      </DialogContent>
      <DialogActions sx={{px: 2, py: 1}}>
        <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        <Button type="submit" variant={'modal'}>
          {selectedOrganization?.id ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
